import { GridContainerProps } from "@insite/mobius/GridContainer";
import LoadingSpinner from "@insite/mobius/LoadingSpinner";
import React, { useEffect, useRef, useState } from "react";

interface LazyLoadWrapperProps {
    children: React.ReactNode;
    onVisible?: () => void;
    setIsVisible?: (visible: boolean) => void;
}

export interface LazyLoadWrapperStyles {
    mainContainer?: GridContainerProps;
}

export const lazyLoadWrapperStyles: LazyLoadWrapperStyles = {
    mainContainer: {
        style: {
            textAlign: "center",
        },
    },
};

const styles = lazyLoadWrapperStyles;

const LazyLoadWrapper: React.FC<LazyLoadWrapperProps> = ({ children, onVisible, setIsVisible }) => {
    const [isVisible, setIsVisibleState] = useState(false);
    const [hasTriggered, setHasTriggered] = useState(false);
    const ref = useRef<HTMLDivElement>(null);

    useEffect(() => {
        if (hasTriggered) return;

        const triggerVisibility = () => {
            if (!hasTriggered) {
                setHasTriggered(true);
                setIsVisibleState(true);
                setIsVisible?.(true);
                onVisible?.();
            }
        };

        const checkVisibility = () => {
            if (ref.current) {
                const rect = ref.current.getBoundingClientRect();
                const windowHeight = window.innerHeight || document.documentElement.clientHeight;

                // If element is already in viewport, trigger immediately
                if (rect.top < windowHeight && rect.bottom > 0) {
                    triggerVisibility();
                    return true;
                }
            }
            return false;
        };

        // Use requestAnimationFrame to ensure DOM is ready
        requestAnimationFrame(() => {
            if (hasTriggered) return;

            const isAlreadyVisible = checkVisibility();

            if (!isAlreadyVisible) {
                // Set up IntersectionObserver for future visibility
                const observer = new IntersectionObserver(
                    entries => {
                        if (entries[0].isIntersecting) {
                            triggerVisibility();
                            observer.disconnect();
                        }
                    },
                    { threshold: 0.1, rootMargin: "50px" },
                );

                if (ref.current) {
                    observer.observe(ref.current);
                }

                return () => {
                    observer.disconnect();
                };
            }
        });

        // Fallback check with a small delay
        const timeoutId = setTimeout(() => {
            if (!hasTriggered) {
                checkVisibility();
            }
        }, 100);

        return () => {
            clearTimeout(timeoutId);
        };
    }, [hasTriggered, onVisible, setIsVisible]);

    return (
        <div ref={ref}>
            {isVisible ? (
                children
            ) : (
                <div {...styles.mainContainer}>
                    <LoadingSpinner />
                </div>
            )}
        </div>
    );
};

export default LazyLoadWrapper;